import { Link } from "react-router-dom";
import { Heart, Eye, ShoppingBag, Star } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";

export default function ProductCard({ product, onQuickView }) {
  const { addToCart, toggleWishlist, wishlist } = useCart();
  const wished = wishlist.includes(product.id);

  const handleAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    addToCart(product, null, 1);
    toast.success("Added to your cart", { description: product.name });
  };

  const handleWish = (e) => {
    e.preventDefault(); e.stopPropagation();
    toggleWishlist(product.id);
    toast(wished ? "Removed from wishlist" : "Saved to wishlist ❤︎");
  };

  const handleQuick = (e) => {
    e.preventDefault(); e.stopPropagation();
    onQuickView?.(product);
  };

  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}
      className="group block candle-card rounded-2xl overflow-hidden">
      <div className="relative overflow-hidden aspect-[4/5] bg-[hsl(var(--muted))]">
        <img src={product.image} alt={product.name} loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {product.tag && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-medium tracking-wider uppercase bg-[hsl(var(--background))]/90 border border-[hsl(var(--border))]">
            {product.tag}
          </span>
        )}
        <button data-testid={`wishlist-toggle-${product.id}`} onClick={handleWish}
          className="absolute top-3 right-3 p-2 rounded-full bg-[hsl(var(--background))]/85 backdrop-blur hover:bg-[hsl(var(--background))] transition">
          <Heart className={`w-4 h-4 ${wished ? "fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "text-foreground"}`} />
        </button>

        <div className="absolute inset-x-3 bottom-3 flex gap-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <button data-testid={`quick-view-button-${product.id}`} onClick={handleQuick}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[hsl(var(--background))]/95 backdrop-blur text-xs font-medium border border-[hsl(var(--border))] hover:bg-[hsl(var(--background))]">
            <Eye className="w-3.5 h-3.5" /> Quick View
          </button>
          <button data-testid={`add-to-cart-button-${product.id}`} onClick={handleAdd}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs font-medium hover:opacity-90">
            <ShoppingBag className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-1 mb-1.5">
          <Star className="w-3.5 h-3.5 fill-candle-gold text-candle-gold" />
          <span className="text-xs font-medium">{product.rating}</span>
          <span className="text-xs text-muted-foreground">({product.reviewsCount})</span>
        </div>
        <h3 className="font-serif text-lg sm:text-xl font-medium leading-tight line-clamp-1">{product.name}</h3>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{product.scent}</p>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="font-serif text-lg font-semibold text-[hsl(var(--foreground))]">{formatINR(product.price)}</span>
          {product.originalPrice && product.originalPrice > product.price && (
            <span className="text-xs text-muted-foreground line-through">{formatINR(product.originalPrice)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
